import { forwardRef, InputHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon?: ReactNode;
  error?: string;
  rightElement?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, icon, error, rightElement, className, id, ...props }, ref) => {
    const inputId = id || label.toLowerCase().replace(/\s+/g, "-");

    return (
      <div className="space-y-2">
        <label
          htmlFor={inputId}
          className="block text-[12px] sm:text-[13px] font-semibold"
          style={{ color: '#0B1220' }}
        >
          {label}
        </label>
        <div className="relative">
          {icon && (
            <div
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              "w-full h-[46px] sm:h-12 lg:h-11 rounded-[12px] border bg-white px-4 text-sm transition-all duration-200",
              "focus:outline-none",
              "placeholder:text-[#A7B0C0]",
              icon && "pl-11",
              rightElement && "pr-12",
              error
                ? "border-destructive"
                : "",
              className
            )}
            style={{
              borderColor: error ? undefined : '#E6EAF2',
              boxShadow: 'none',
              color: '#0B1220',
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLInputElement;
              if (document.activeElement !== target && !error) {
                target.style.borderColor = '#D8E2F2';
              }
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLInputElement;
              if (document.activeElement !== target && !error) {
                target.style.borderColor = '#E6EAF2';
              }
            }}
            onFocus={(e) => {
              if (!error) {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = 'rgba(31, 107, 255, 0.65)';
              }
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              if (!error) {
                e.target.style.borderColor = '#E6EAF2';
              }
              props.onBlur?.(e);
            }}
            placeholder={props.placeholder}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            {...props}
          />
          {rightElement && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
              {rightElement}
            </div>
          )}
        </div>
        {error && (
          <p
            id={`${inputId}-error`}
            className="text-[13px] text-destructive"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = "TextField";
